export type GeoPoint = { lat: number; lng: number };

export type SegmentViasMap = Record<number, GeoPoint[]>;

export type ViaWithRatio = GeoPoint & { __ratio: number };

/** Default epsilon for deduping consecutive waypoints */
export const DEFAULT_DEDUPE_EPS = 1e-6;

export const prepareSort = (sort?: `${string}:${'asc' | 'desc'}`): `${string}:${'asc' | 'desc'}` => {
  if (!sort) {
    return 'date:desc';
  }

  if (sort.includes('suppliers')) {
    return sort.replace('suppliers', 'supplier') as `${string}:${'asc' | 'desc'}`;
  }

  if (sort.includes('drivers')) {
    return sort.replace('drivers', 'driver') as `${string}:${'asc' | 'desc'}`;
  }

  if (sort.includes('vehicles')) {
    return sort.replace('vehicles', 'vehicle') as `${string}:${'asc' | 'desc'}`;
  }

  return sort;
};

export function decodePolyline(str: string, precision = 5): Array<[number, number]> {
  let index = 0,
    lat = 0,
    lng = 0;

  const coordinates: Array<[number, number]> = [];
  const factor = Math.pow(10, precision);

  while (index < str.length) {
    let b: number,
      shift = 0,
      result = 0;

    do {
      b = str.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);

    const dlat = result & 1 ? ~(result >> 1) : result >> 1;

    lat += dlat;

    shift = 0;
    result = 0;

    do {
      b = str.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);

    const dlng = result & 1 ? ~(result >> 1) : result >> 1;

    lng += dlng;

    coordinates.push([lat / factor, lng / factor]);
  }

  return coordinates;
}

export function distanceToSegment(p: { lat: number; lng: number }, a: [number, number], b: [number, number]): number {
  const x = p.lng,
    y = p.lat;

  const x1 = a[1],
    y1 = a[0],
    x2 = b[1],
    y2 = b[0];

  const A = x - x1,
    B = y - y1,
    C = x2 - x1,
    D = y2 - y1;

  const dot = A * C + B * D;
  const len_sq = C * C + D * D || 1e-12;
  let t = dot / len_sq;

  t = Math.max(0, Math.min(1, t));
  const xx = x1 + C * t,
    yy = y1 + D * t;

  const dx = x - xx,
    dy = y - yy;

  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Get the normalized parametric position of a point along a line segment.
 *
 * Computes the ratio `t ∈ [0, 1]` of the orthogonal projection of `point` onto the segment
 * from `start` → `end`, using a planar approximation where x=lng and y=lat.
 *
 * - t ≈ 0   → near `start`
 * - t ≈ 0.5 → mid-segment
 * - t ≈ 1   → near `end`
 *
 * If the segment has zero length or the math becomes non-finite, returns 0.
 */
export function alongSegmentRatio(start: GeoPoint, end: GeoPoint, point: GeoPoint): number {
  const startLng = start.lng;
  const startLat = start.lat;

  const endLng = end.lng;
  const endLat = end.lat;

  const pointLng = point.lng;
  const pointLat = point.lat;

  // Segment vector (start → end)
  const vectorLng = endLng - startLng;
  const vectorLat = endLat - startLat;

  // Early exit for a degenerate segment
  const segmentLengthSquared = vectorLng * vectorLng + vectorLat * vectorLat;

  if (segmentLengthSquared === 0) {
    return 0;
  }

  // Vector from start to point
  const fromStartToPointLng = pointLng - startLng;
  const fromStartToPointLat = pointLat - startLat;

  // Project: t = (AP · AB) / |AB|^2
  const dotProduct = fromStartToPointLng * vectorLng + fromStartToPointLat * vectorLat;
  const rawRatio = dotProduct / segmentLengthSquared;

  // Guard non-finite results and clamp to [0, 1]
  if (!Number.isFinite(rawRatio)) {
    return 0;
  }

  const clampedRatio = Math.max(0, Math.min(1, rawRatio));

  return clampedRatio;
}

/**
 * Returns vias sorted by their position along segment A→B (using {@link alongSegmentRatio}).
 * Sorting guarantees correct waypoint ordering for routing engines.
 */
export function sortViasAlongSegment(start: GeoPoint, end: GeoPoint, vias: GeoPoint[] = []): GeoPoint[] {
  // Early exit for empty or single-item arrays
  if (vias.length <= 1) {
    return vias;
  }

  // Calculate ratios in-place and store with original points
  const viasWithRatios: ViaWithRatio[] = new Array(vias.length);

  for (let i = 0; i < vias.length; i++) {
    viasWithRatios[i] = {
      ...vias[i],
      __ratio: alongSegmentRatio(start, end, vias[i]),
    };
  }

  // Sort by ratio
  viasWithRatios.sort((viaA, viaB) => viaA.__ratio - viaB.__ratio);

  // Remove ratio property and return clean GeoPoints
  const sortedVias: GeoPoint[] = new Array(vias.length);

  for (let i = 0; i < vias.length; i++) {
     
    const { __ratio, ...cleanPoint } = viasWithRatios[i];

    sortedVias[i] = cleanPoint;
  }

  return sortedVias;
}

/**
 * Normalizes a segment→vias map so that for every segment i (between stops[i] and stops[i+1]),
 * its via list is sorted in A→B order. Non-existent or out-of-bounds segments are skipped.
 */
export function normalizeViaMapForStops(stops: GeoPoint[], vmap: SegmentViasMap): SegmentViasMap {
  const out: SegmentViasMap = { ...vmap };

  for (const k of Object.keys(out)) {
    const i = Number(k);
    const a = stops[i];
    const b = stops[i + 1];

    if (!a || !b) {
      continue;
    }

    out[i] = sortViasAlongSegment(a, b, out[i] || []);
  }

  return out;
}

/**
 * Removes consecutive duplicate points within a tiny epsilon (lat/lng).
 * Useful before sending waypoints to a routing engine.
 */
export function dedupeConsecutiveWaypoints(points: GeoPoint[], eps: number = DEFAULT_DEDUPE_EPS): GeoPoint[] {
  const out: GeoPoint[] = [];

  for (const p of points) {
    const last = out[out.length - 1];

    if (last && Math.abs(last.lat - p.lat) < eps && Math.abs(last.lng - p.lng) < eps) {
      continue;
    }

    out.push(p);
  }

  return out;
}
